import axios from 'axios'
import { LOGOUT, EXPIRED_SESSION } from '@/vuex/types'
import { app } from '@/main'

// timeout in msec
let instance = axios.create({
  baseURL: '/api',
  timeout: 600000
})

instance.interceptors.request.use(function (config) {
  if (app.$router.currentRoute.query && app.$router.currentRoute.query.token) {
    // For the feature: login as user from admin app.
    window.sessionStorage.setItem('token', app.$router.currentRoute.query.token)
  }
  let token = window.sessionStorage.getItem('token')
  if (token) {
    config.headers.common['Authorization'] = 'Bearer ' + token
  }
  let loadingDiv = document.createElement('div')
  let spinnerDiv = document.createElement('div')
  let bounceDiv1 = document.createElement('div')
  let bounceDiv2 = document.createElement('div')
  let bounceDiv3 = document.createElement('div')
  loadingDiv.id = 'loadingDiv'
  spinnerDiv.className = 'spinner'
  loadingDiv.className = 'loading'
  bounceDiv1.className = 'bounce1'
  bounceDiv2.className = 'bounce2'
  bounceDiv3.className = 'bounce3'
  let exists = document.getElementById('loadingDiv')
  if (!exists) {
    document.body.appendChild(loadingDiv)
    loadingDiv.appendChild(spinnerDiv)
    spinnerDiv.appendChild(bounceDiv1)
    spinnerDiv.appendChild(bounceDiv2)
    spinnerDiv.appendChild(bounceDiv3)
    loadingDiv.setAttribute('data-requests', 1)
    loadingDiv.setAttribute('data-responses', 0)
  } else {
    let count = parseInt(exists.getAttribute('data-requests'))
    exists.setAttribute('data-requests', count + 1)
  }
  return config
}, function (error) {
  return Promise.reject(error.response)
})

instance.interceptors.response.use(function (response) {
  let loadingDivRemove = document.getElementById('loadingDiv')
  if (loadingDivRemove) {
    let count = parseInt(loadingDivRemove.getAttribute('data-responses'))
    loadingDivRemove.setAttribute('data-responses', count + 1)
    let requests = parseInt(loadingDivRemove.getAttribute('data-requests'))
    let responses = parseInt(loadingDivRemove.getAttribute('data-responses'))
    if (requests === responses) {
      loadingDivRemove.parentNode.removeChild(loadingDivRemove)
    }
  }
  return response
}, function (error) {
  let loadingDivRemove = document.getElementById('loadingDiv')
  if (loadingDivRemove) {
    let count = parseInt(loadingDivRemove.getAttribute('data-responses'))
    loadingDivRemove.setAttribute('data-responses', count + 1)
    let requests = parseInt(loadingDivRemove.getAttribute('data-requests'))
    let responses = parseInt(loadingDivRemove.getAttribute('data-responses'))
    if (requests === responses) {
      loadingDivRemove.parentNode.removeChild(loadingDivRemove)
    }
  }
  if (error.response && error.response.status === 401) {
    app.$store.commit(EXPIRED_SESSION, true)
    app.$store.dispatch(LOGOUT)
    let redirect = app.$router.currentRoute.path
    if (!redirect.includes('login') && !redirect.includes('forgot-password') && !redirect.includes('reset-password') && !redirect.includes('/feed-back/')) {
      redirect = '/login'
      app.$router.push(redirect)
    }
    return redirect
  } else {
    return Promise.reject(error.response)
  }
})

class Api {
  async getForm (permalink, params = {}, auth = 'admin') {
    try {
      let { data } = await instance.get(`${auth}/form`, {
        params: {
          permalink,
          ...params
        }
      })
      return data.form
    } catch (err) {
      throw err
    }
  }

  async submitForm (formData, method = 'post', auth = 'admin') {
    try {
      let { data } = await instance[method](`${auth}/form`, formData)
      return data.result
    } catch (err) {
      throw err
    }
  }

  async getReportName (uuid) {
    let { data } = await instance.get(`admin/object/reportName?reportTypeUuid=${uuid}`)
    return data ? data.object : ''
  }

  async refreshLoggedInUser () {
    let { data } = await instance.post('admin/object/refreshLoggedInUser')
    if (!data) throw new Error('Cannot refresh user.')
    else return data
  }

  async loadSettings (domain) {
    let { data } = await instance.post('public/config')
    return data
  }

  async getSidebar () {
    let { data } = await instance.get('/admin/object/sidebar')
    if (!data) {
      return []
    } else {
      return data.object
    }
  }

  async getUser (account) {
    let { data } = await instance.get('/admin/object/account', {
      params: { account }
    })
    return data.object
  }

  async loginAsUser (account) {
    let { data } = await instance.post('/admin/object/loginAsUser', {
      uuid: account.uuid
    })
    return data.object
  }

  async getContractor (account) {
    let { data } = await instance.get('/admin/object/contractor', {
      params: { account }
    })
    return data.object
  }

  async geocode (address) {
    let { data } = await instance.get('/admin/object/geocode', {
      params: { address }
    })
    return data.object
  }

  async getCompany (company) {
    let { data } = await instance.get('/admin/object/company', {
      params: { company }
    })
    return data.object
  }

  async getAllStaff () {
    let { data } = await instance.get('/admin/object/allStaff')
    return data.object
  }

  async getActivWorkOrders () {
    let { data } = await instance.get('/admin/object/activWorkOrders')
    return data.object
  }

  async getEsmItems () {
    let { data } = await instance.get('/admin/object/esmItems')
    return data.object
  }

  async getReportPdfText (reportTypeUuid) {
    let { data } = await instance.get('/admin/object/reportPdfText', {
      params: { reportTypeUuid }
    })
    return data.object
  }

  async getReportCost (options) {
    let { data } = await instance.get('/admin/object/reportCost', {
      params: { ...options }
    })
    return data.object
  }

  async getPropertyCost (options) {
    let { data } = await instance.get('/admin/object/propertyCost', {
      params: { ...options }
    })
    return data.object
  }

  async getCustomReportTypeFields (reportTypeUuid) {
    let { data } = await instance.get('/admin/object/customReportTypeFields', {
      params: { reportTypeUuid }
    })
    return data.object
  }

  async getApplicableItems (applicableItemTemplateUuid) {
    let { data } = await instance.get('/admin/object/applicableItems', {
      params: { applicableItemTemplateUuid }
    })
    return data.object
  }

  async getFinding (esmItemTemplateuuid) {
    let { data } = await instance.get('/admin/object/finding', {
      params: { esmItemTemplateuuid }
    })
    return data.object
  }

  async getRecentActivity (options) {
    let { data } = await instance.get('/admin/object/recentActivity', {
      params: { options }
    })
    return data.object
  }

  async getProperty (propertyUuid) {
    let { data } = await instance.get('/admin/object/property', {
      params: { propertyUuid }
    })
    return data.object
  }

  async getInUseReports () {
    let { data } = await instance.get('/admin/object/inUseReports')
    if (!data) {
      return []
    } else {
      return data.object
    }
  }

  async closeReport (openReportUuid) {
    let { data } = await instance.post('/admin/object/closeReport', {
      openReportUuid
    })
    return data.object
  }

  async getProperties (companyUuid) {
    let { data } = await instance.get('/admin/object/properties', {
      params: { companyUuid }
    })
    return data.object
  }

  async getManagerProperties (managerUuid) {
    let { data } = await instance.get('/admin/object/managerProperties', {
      params: { managerUuid }
    })
    return data.object
  }

  async checkUsername (username) {
    let { data } = await instance.post('admin/object/uniqueUsername', {
      username
    })
    return data.object
  }

  async getDashboard (date) {
    let { data } = await instance.post('admin/object/dashboard', { date })
    return data.object
  }

  async getStats (payload) {
    let { data } = await instance.post('admin/object/stats', { payload })
    return data.object
  }

  async hasMultipleAccounts (email) {
    let { data } = await instance.post('public/hasMultipleAccounts', { email })
    return data.result
  }

  async logout () {
    let logout = await instance.post('public/logout')
    return logout
  }

  async getDatabaseItems (options) {
    // Added ocManagerUuid to make Manager name as a hyperlink to edit its details under DatabaseSearch.
    let { data } = await instance.post(`admin/database/${options.dataName}`, {
      ocManagerUuid: options.ocManagerUuid,
      searchForm: options.searchForm,
      offset: options.offset,
      limit: options.limit,
      newSearch: options.newSearch,
      sortColumn: options.sortColumn,
      sortDirection: options.sortDirection
    })
    return { ...data, offset: options.offset, limit: options.limit, permalink: options.dataName }
  }

  async saveNewVal (options) {
    let { data } = await instance.post('admin/object/saveNewVal', {
      modelName: options.modelName,
      fieldName: options.fieldName,
      value: options.value
    })
    return data.object
  }

  async convertTradesReport (TradesReportUuid) {
    let { data } = await instance.post('admin/object/convertTradesReport', {
      TradesReportUuid
    })
    return data.object
  }

  async duplicateAsbestosReport (AsbestosReportUuid) {
    let { data } = await instance.post('admin/object/duplicateAsbestosReport', {
      AsbestosReportUuid
    })
    return data.object
  }

  async duplicateDefectsReport (DefectsReportUuid) {
    let { data } = await instance.post('admin/object/duplicateDefectsReport', {
      DefectsReportUuid
    })
    return data.object
  }

  async duplicateEsmReport (EsmReportUuid) {
    let { data } = await instance.post('admin/object/duplicateEsmReport', {
      EsmReportUuid
    })
    return data.object
  }

  async duplicateIncidentReport (IncidentReportUuid) {
    let { data } = await instance.post('admin/object/duplicateIncidentReport', {
      IncidentReportUuid
    })
    return data.object
  }

  async duplicateInspectionReport (InspectionReportUuid) {
    let { data } = await instance.post('admin/object/duplicateInspectionReport', {
      InspectionReportUuid
    })
    return data.object
  }

  async duplicateInsuranceReport (InsuranceReportUuid) {
    let { data } = await instance.post('admin/object/duplicateInsuranceReport', {
      InsuranceReportUuid
    })
    return data.object
  }

  async duplicateOhsReport (OhsReportUuid) {
    let { data } = await instance.post('admin/object/duplicateOhsReport', {
      OhsReportUuid
    })
    return data.object
  }

  async duplicateQuote (QuoteUuid) {
    let { data } = await instance.post('admin/object/duplicateQuote', {
      QuoteUuid
    })
    return data.object
  }

  async duplicatePropertyPhotosReport (PropertyPhotosReportUuid) {
    let { data } = await instance.post('admin/object/duplicatePropertyPhotosReport', {
      PropertyPhotosReportUuid
    })
    return data.object
  }

  async duplicateTaxInvoiceRequest (TaxInvoiceRequestUuid) {
    let { data } = await instance.post('admin/object/duplicateTaxInvoiceRequest', {
      TaxInvoiceRequestUuid
    })
    return data.object
  }

  async getReports (options) {
    let { data } = await instance.post(`admin/report/${options.reportType}`, {
      searchForm: options.searchForm,
      offset: options.offset,
      limit: options.limit,
      newSearch: options.newSearch,
      date: options.date,
      selectedMonth: options.selectedMonth,
      client: options.client,
      reportUuid: options.reportUuid
    })
    return { ...data, offset: options.offset, limit: options.limit, permalink: options.reportType }
  }

  // async getMonthlyReports (options) {
  //   let { data } = await instance.post(`admin/report/month/${options.reportType}`, {
  //     selectedMonth: options.selectedMonth,
  //   })
  //   return { ...data, offset: options.offset, limit: options.limit, permalink: options.reportType }
  // }

  async getActiveReportDates (options) {
    let { data } = await instance.post(`admin/report/dates/${options.reportType}`, {
      selectedMonth: options.selectedMonth
    })

    return { ...data }
  }

  async setInvoiceStatusOCManager (options) {
    let { data } = await instance.post('admin/object/setOCManagerInvoiced', options)
    return data.object
  }

  async removeContractor (contractorUuid, propertyUuid) {
    let { data } = await instance.post('admin/object/removeContractor', { contractorUuid, propertyUuid })
    return data.object
  }

  async getDirectory () {
    let { data } = await instance.get('admin/object/directory')
    return data.object
  }

  async getReportInfo () {
    let { data } = await instance.get('admin/object/reportInfo')
    return data.object
  }

  async getQuotes (options) {
    let { data } = await instance.post(`admin/quotes/${options.dataName}`, {
      searchForm: options.searchForm,
      params: options.params,
      offset: options.offset,
      limit: options.limit,
      newSearch: options.newSearch,
      sortColumn: options.sortColumn,
      sortDirection: options.sortDirection
    })
    return { ...data, offset: options.offset, limit: options.limit, permalink: options.dataName }
  }
  // async getEPMA (options) {
  //   console.log("API CALL")
  //   let { data } = await instance.post(`admin/epma/${options.dataName}`, {
  //     searchForm: options.searchForm,
  //     params: options.params,
  //     offset: options.offset,
  //     limit: options.limit,
  //     newSearch: options.newSearch,
  //     sortColumn: options.sortColumn,
  //     sortDirection: options.sortDirection
  //   })
  //   return { ...data, offset: options.offset, limit: options.limit, permalink: options.dataName }
  // }

  async getQuoteNotes (quoteUuid) {
    let { data } = await instance.get(`admin/object/quoteNotes`, {
      params: { quoteUuid }
    })
    return data.object
  }

  async databaseSearch (payload) {
    let { data } = await instance.post('admin/object/databaseSearch', payload)
    return data.object
  }

  async getTicket (ticketId) {
    let { data } = await instance.get('admin/object/ticket', {
      params: { ticketId }
    })
    return data.object
  }

  async closeTicket (ticketId) {
    let { data } = await instance.post('admin/object/ticketClose', { ticketId })
    return data.object
  }

  async reopenTicket (ticketId) {
    let { data } = await instance.post('admin/object/ticketReopen', { ticketId })
    return data.object
  }

  async deleteTicket (ticketId) {
    let { data } = await instance.post('admin/object/ticketDelete', { ticketId })
    return data.object
  }

  async removeTicketPriority (ticketId) {
    let { data } = await instance.post('admin/object/ticketRemovePriority', { ticketId })
    return data.object
  }

  async addTicketPriority (ticketDetail) {
    let { data } = await instance.post('admin/object/ticketAddPriority', { ticketDetail })
    return data.object
  }

  async getCards (options) {
    let { data } = await instance.post(`admin/cards/${options.cards}`, {
      searchForm: options.searchForm,
      sortDirection: options.sortDirection,
      sortColumn: options.sortColumn,
      offset: options.offset,
      limit: options.limit,
      newSearch: options.newSearch
    })
    return { ...data, offset: options.offset, limit: options.limit, permalink: options.cards }
  }
}
export default Api



// WEBPACK FOOTER //
// ./src/vuex/api/index.js